import {useEffect, useState} from 'react';
import {useAccountStatus} from './hooks/useAccountStatus';
import {useTalesList} from './hooks/useTalesList';
import {trackEvent} from './utils/analytics';
import Header from './components/layout/Header';
import Landing from './components/landing/Landing';
import MyContentPage from './components/pages/MyContentPage';
import Footer from './components/layout/Footer';
import {getPlans} from './api/plans';
import {getPageConfig} from './config/pageConfigs';
import {HERO_VARIANTS, resolveHeroVariantKey} from './config/heroConfigs';
import SkazkiCard from './components/skazki/SkazkiCard';

function pluralizeTales(count) {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return `${count} сказка`;
    if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return `${count} сказки`;
    return `${count} сказок`;
}

// Главное приложение — сказки. `view` приходит из роутера:
//   'landing' — лендинг на «/» (только промо, без формы создания),
//   'app'     — приложение на «/app» (форма создания / плеер),
//   'orders'  — «Мои заказы».
export default function App({view = 'landing', skazkiAction, skazkiTaleId}) {
    const pageConfig = getPageConfig();

    const {
        skazkiCredits,
        subscription,
        linkedTelegramId,
        linkedMaxId,
        refetch: fetchAccountStatus
    } = useAccountStatus({enabled: view !== 'landing'});
    // Tales now live in the /v1 service (a different table than celebs), so the
    // "Мои заказы" list is sourced from GET /v1/tales, not account-status.
    const {tales: skazkiTales, refetch: fetchTales} = useTalesList({enabled: view === 'orders'});
    const [plans, setPlans] = useState({subscriptions: [], topups: []});
    // Playback survives navigation without any root hook: the audio element
    // lives at module level in utils/skazkiAudio. Subscribing here would
    // re-render the whole app on every timeupdate (~4/сек) while playing.
    const [postPurchaseOpen, setPostPurchaseOpen] = useState(false);
    // Pin the hero variant once per page load (UTM-driven, otherwise random) so
    // it doesn't re-roll on every re-render.
    const [heroKey] = useState(() => resolveHeroVariantKey());

    useEffect(() => {
        trackEvent('skazki_tab_open');
        if (view === 'landing') trackEvent(`skazki_hero_${heroKey}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Set meta tags
    useEffect(() => {
        document.title = pageConfig.title;
        document.querySelector('meta[name="description"]')?.setAttribute('content', pageConfig.description);
    }, [pageConfig]);

    // Fetch pricing plans from API — only the tale counts matter for skazki.
    useEffect(() => {
        getPlans('skazki').then(data => {
            const mapPlan = (p) => ({
                id: p.id,
                name: p.name,
                tales: p.skazki_credits || 0,
                tales_text: pluralizeTales(p.skazki_credits || 0),
                price: p.price_rub,
                oldPrice: p.old_price_rub,
                discount: p.discount_percent || null,
                kind: p.kind || 'topup',
                billingPeriod: p.billing_period || null,
            });
            setPlans({
                subscriptions: (data.subscriptions || []).map(mapPlan),
                topups: (data.topups || []).map(mapPlan),
            });
        }).catch(console.error);
    }, []);

    const goLanding = () => {
        window.location.assign('/');
    };

    // CTA «Создать сказку» → отдельная страница приложения.
    const goApp = () => {
        window.location.assign('/app');
    };

    // CTA «Создать сказку» на лендинге: фиксируем старт сценария до перехода.
    const goAppFromLanding = () => {
        trackEvent('skazki_start');
        goApp();
    };

    const goOrders = () => {
        window.location.assign('/orders');
    };

    // ── Лендинг на «/» — только промо, форма создания живёт на «/app». ──
    if (view === 'landing') {
        return (
            <div className="min-h-screen bg-white font-sans overflow-x-hidden relative">
                <div className="relative z-10">
                    {/* Шапка прозрачная и лежит поверх фото героя (фото уходит под неё). */}
                    <div className="absolute top-0 inset-x-0 z-20">
                        <Header onHomeClick={goLanding} onOrdersClick={goOrders} onCtaClick={goAppFromLanding} overlay/>
                    </div>

                    <Landing onCtaClick={goAppFromLanding} heroVariant={HERO_VARIANTS[heroKey]}/>

                    <Footer onOrdersClick={goOrders} onGenerateClick={goLanding}/>
                </div>
            </div>
        );
    }

    if (view === 'orders') {
        return (
            <div className="min-h-screen bg-white font-sans overflow-x-hidden relative">
                <div className="relative z-10">
                    <Header onHomeClick={goLanding} onOrdersClick={goOrders} onCtaClick={goApp}/>
                    <main className="pt-6">
                        <MyContentPage
                            skazkiTales={skazkiTales}
                            skazkiCredits={skazkiCredits}
                            subscription={subscription}
                            plans={plans}
                            linkedTelegramId={linkedTelegramId}
                            linkedMaxId={linkedMaxId}
                            fetchAccountStatus={fetchAccountStatus}
                            fetchTales={fetchTales}
                            onSkazkiOpen={(taleId) => {
                                window.location.assign(`/skazki/s/${taleId}`);
                            }}
                            onSkazkiClick={goApp}
                            onBuyCredits={goApp}
                        />
                    </main>
                    <Footer onOrdersClick={goOrders} onGenerateClick={goLanding}/>
                </div>
            </div>
        );
    }

    // ── Приложение на «/app» — форма создания / плеер, без лендинга. ──
    return (
        <div className="min-h-screen bg-white font-sans overflow-x-hidden relative">
            <div className="relative z-10">
                <Header onHomeClick={goLanding} onOrdersClick={goOrders}/>

                <main className="pt-6 md:pt-10 skazki-page">
                    <SkazkiCard
                        initialAction={skazkiAction}
                        initialTaleId={skazkiTaleId || null}
                    />
                </main>

                <Footer onOrdersClick={goOrders} onGenerateClick={goLanding}/>
            </div>

            {/* Post-Purchase Suggestion Modal (сказки) */}
            {postPurchaseOpen && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-[24px] p-8 max-w-md w-full shadow-2xl text-center">
                        <div
                            className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-center">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"
                                 strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6L9 17l-5-5"/>
                            </svg>
                        </div>

                        <h3 className="text-2xl font-bold text-brand-dark mb-2">
                            Оплата прошла!
                        </h3>
                        <p className="text-brand-dark/60 mb-6">
                            Сказки зачислены на ваш баланс. Создайте сказку прямо сейчас!
                        </p>

                        <button
                            onClick={() => {
                                setPostPurchaseOpen(false);
                                goApp();
                            }}
                            className="w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-base font-semibold hover:opacity-95 transition mb-3"
                        >
                            Создать сказку
                        </button>

                        <button
                            onClick={() => {
                                setPostPurchaseOpen(false);
                                goOrders();
                            }}
                            className="w-full py-3 text-brand-dark/60 hover:text-brand-dark text-sm font-medium transition"
                        >
                            Перейти к заказам
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
